/**
 * Detection Predictor
 *
 * Predicts missing ball/paddle positions using velocity extrapolation.
 * Tracks ancestorDepth to limit how far predictions can chain.
 *
 * Two modes:
 * 1. Sequential: Process frames in order, keeps only t-1 and t-2 state
 * 2. Random access: Jump to any frame, uses recursive cache (max 2*N lookback)
 */

import { getPrimaryDetections, isValidDetection, predictDetection } from './pickleballInterpolation.js';

// Default max ancestor depth - stop predicting after this many missing frames
export const DEFAULT_MAX_ANCESTOR_DEPTH = 5;

/**
 * Streaming predictor for sequential frame processing.
 * Efficient O(1) per frame, stores only t-1 and t-2 state.
 */
export class StreamingDetectionPredictor {
  /**
   * @param {number} maxAncestorDepth - Stop predicting after this many consecutive missing frames
   */
  constructor(maxAncestorDepth = DEFAULT_MAX_ANCESTOR_DEPTH) {
    this.maxAncestorDepth = maxAncestorDepth;
    this.reset();
  }

  /**
   * Reset state (call when starting new sequence)
   */
  reset() {
    // State for t-1 frame
    this.prevBall = null;      // { detection, ancestorDepth }
    this.prevPaddle = null;
    // State for t-2 frame
    this.prevPrevBall = null;
    this.prevPrevPaddle = null;
  }

  /**
   * Process next frame in sequence.
   * Call this for each frame in order (0, 1, 2, 3, ...).
   *
   * @param {Object} frame - Frame data with ball/paddle detections
   * @returns {Object} { ball, paddle, ballAncestorDepth, paddleAncestorDepth, ballWasPredicted, paddleWasPredicted }
   */
  processFrame(frame) {
    const { ball: rawBall, paddle: rawPaddle } = getPrimaryDetections(frame);

    // Process ball
    const ballResult = this._processDetection(
      rawBall,
      this.prevBall,
      this.prevPrevBall
    );

    // Process paddle
    const paddleResult = this._processDetection(
      rawPaddle,
      this.prevPaddle,
      this.prevPrevPaddle
    );

    // Shift state: t-2 = t-1, t-1 = current
    this.prevPrevBall = this.prevBall;
    this.prevBall = { detection: ballResult.detection, ancestorDepth: ballResult.ancestorDepth };

    this.prevPrevPaddle = this.prevPaddle;
    this.prevPaddle = { detection: paddleResult.detection, ancestorDepth: paddleResult.ancestorDepth };

    return {
      ball: ballResult.detection,
      paddle: paddleResult.detection,
      ballAncestorDepth: ballResult.ancestorDepth,
      paddleAncestorDepth: paddleResult.ancestorDepth,
      ballWasPredicted: ballResult.wasPredicted,
      paddleWasPredicted: paddleResult.wasPredicted
    };
  }

  /**
   * Get current state for velocity calculation (t-1 predictions)
   * @returns {Object} { ball, paddle } - Previous frame's predictions
   */
  getPrevState() {
    return {
      ball: this.prevBall?.detection || null,
      paddle: this.prevPaddle?.detection || null
    };
  }

  /**
   * Get t-2 state for acceleration calculation
   * @returns {Object} { ball, paddle } - Frame before previous predictions
   */
  getPrevPrevState() {
    return {
      ball: this.prevPrevBall?.detection || null,
      paddle: this.prevPrevPaddle?.detection || null
    };
  }

  _processDetection(rawDetection, prev, prevPrev) {
    // If raw detection is valid, use it directly
    if (isValidDetection(rawDetection)) {
      return {
        detection: rawDetection,
        ancestorDepth: 0,
        wasPredicted: false
      };
    }

    // No previous data to predict from
    if (!prev || prev.detection === null) {
      return {
        detection: null,
        ancestorDepth: Infinity,
        wasPredicted: false
      };
    }

    // Check if we've exceeded max ancestor depth
    const prevDepth = prev.ancestorDepth;
    const prevPrevDepth = prevPrev?.ancestorDepth ?? 0;
    const newDepth = Math.max(prevDepth, prevPrevDepth) + 1;

    if (newDepth > this.maxAncestorDepth) {
      return {
        detection: null,
        ancestorDepth: Infinity,
        wasPredicted: false
      };
    }

    // Predict using velocity extrapolation
    const predicted = predictDetection(
      prevPrev?.detection || null,
      prev.detection
    );

    if (predicted === -1) {
      return {
        detection: null,
        ancestorDepth: Infinity,
        wasPredicted: false
      };
    }

    return {
      detection: predicted,
      ancestorDepth: newDepth,
      wasPredicted: true
    };
  }
}

/**
 * Get detection at arbitrary frame using recursive cache.
 * Use when jumping to random frames (visualizer navigation, batch processing start).
 *
 * @param {Array} frames - All frames array
 * @param {number} frameIdx - Target frame index
 * @param {string} type - 'ball' or 'paddle'
 * @param {Object} options - { maxAncestorDepth, cache }
 * @returns {Object} { detection, ancestorDepth, wasPredicted }
 */
export function getDetectionAtFrame(frames, frameIdx, type, options = {}) {
  const maxAncestorDepth = options.maxAncestorDepth ?? DEFAULT_MAX_ANCESTOR_DEPTH;
  const cache = options.cache ?? {};

  const cacheKey = `${frameIdx}_${type}`;

  // Check cache first
  if (cache[cacheKey] !== undefined) {
    return cache[cacheKey];
  }

  // Base case: out of bounds
  if (frameIdx < 0 || frameIdx >= frames.length) {
    const result = { detection: null, ancestorDepth: Infinity, wasPredicted: false };
    cache[cacheKey] = result;
    return result;
  }

  // Check raw detection
  const { ball, paddle } = getPrimaryDetections(frames[frameIdx]);
  const raw = type === 'ball' ? ball : paddle;

  if (isValidDetection(raw)) {
    const result = { detection: raw, ancestorDepth: 0, wasPredicted: false };
    cache[cacheKey] = result;
    return result;
  }

  // Limit lookback to 2 * maxAncestorDepth frames
  const maxLookback = 2 * maxAncestorDepth;
  const minFrame = frameIdx - maxLookback;

  if (frameIdx <= 0) {
    // Can't look back further
    const result = { detection: null, ancestorDepth: Infinity, wasPredicted: false };
    cache[cacheKey] = result;
    return result;
  }

  // Recursive: get t-1 and t-2
  const prev1 = getDetectionAtFrame(frames, frameIdx - 1, type, { maxAncestorDepth, cache });
  const prev2 = getDetectionAtFrame(frames, frameIdx - 2, type, { maxAncestorDepth, cache });

  // If t-1 has no detection, can't predict
  if (prev1.detection === null) {
    const result = { detection: null, ancestorDepth: Infinity, wasPredicted: false };
    cache[cacheKey] = result;
    return result;
  }

  // Calculate new ancestor depth
  const newDepth = Math.max(prev1.ancestorDepth, prev2.ancestorDepth ?? 0) + 1;

  if (newDepth > maxAncestorDepth) {
    const result = { detection: null, ancestorDepth: Infinity, wasPredicted: false };
    cache[cacheKey] = result;
    return result;
  }

  // Predict using velocity extrapolation
  const predicted = predictDetection(prev2.detection, prev1.detection);

  if (predicted === -1) {
    const result = { detection: null, ancestorDepth: Infinity, wasPredicted: false };
    cache[cacheKey] = result;
    return result;
  }

  const result = { detection: predicted, ancestorDepth: newDepth, wasPredicted: true };
  cache[cacheKey] = result;
  return result;
}

/**
 * Build predictions for a range of frames (for batch processing or visualization).
 * Processes sequentially for efficiency, with optional start cache for random access.
 *
 * @param {Array} frames - All frames array
 * @param {number} startIdx - Start frame index
 * @param {number} endIdx - End frame index (inclusive)
 * @param {Object} options - { maxAncestorDepth }
 * @returns {Array} Array of { ball, paddle, ballAncestorDepth, paddleAncestorDepth, ... } for each frame
 */
export function buildPredictionsForRange(frames, startIdx, endIdx, options = {}) {
  const maxAncestorDepth = options.maxAncestorDepth ?? DEFAULT_MAX_ANCESTOR_DEPTH;
  const results = [];

  // If starting from frame 0, use pure sequential
  if (startIdx === 0) {
    const predictor = new StreamingDetectionPredictor(maxAncestorDepth);
    for (let i = startIdx; i <= endIdx && i < frames.length; i++) {
      results.push(predictor.processFrame(frames[i]));
    }
    return results;
  }

  // Starting from arbitrary frame - use recursive cache to bootstrap
  const cache = {};

  // Get initial state using recursive lookback
  const initBall1 = getDetectionAtFrame(frames, startIdx - 1, 'ball', { maxAncestorDepth, cache });
  const initBall2 = getDetectionAtFrame(frames, startIdx - 2, 'ball', { maxAncestorDepth, cache });
  const initPaddle1 = getDetectionAtFrame(frames, startIdx - 1, 'paddle', { maxAncestorDepth, cache });
  const initPaddle2 = getDetectionAtFrame(frames, startIdx - 2, 'paddle', { maxAncestorDepth, cache });

  // Create predictor with initialized state
  const predictor = new StreamingDetectionPredictor(maxAncestorDepth);
  predictor.prevBall = { detection: initBall1.detection, ancestorDepth: initBall1.ancestorDepth };
  predictor.prevPrevBall = { detection: initBall2.detection, ancestorDepth: initBall2.ancestorDepth };
  predictor.prevPaddle = { detection: initPaddle1.detection, ancestorDepth: initPaddle1.ancestorDepth };
  predictor.prevPrevPaddle = { detection: initPaddle2.detection, ancestorDepth: initPaddle2.ancestorDepth };

  // Process range sequentially
  for (let i = startIdx; i <= endIdx && i < frames.length; i++) {
    results.push(predictor.processFrame(frames[i]));
  }

  return results;
}
